// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

import loadAccountThumbnailsData from "@ces/App/Handlers/XNG/AccountThumbnailsData";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import BackOrdersDashboard from "@ces/XDL/Icons/BackOrdersDashboard";
import QuotesQuickDashboard from "@ces/XDL/Icons/QuotesQuickDashboard";
import SavedListsDashboard from "@ces/XDL/Icons/SavedListsDashboard";
import StatementsDashboard from "@ces/XDL/Icons/StatementsDashboard";

import { XNGAGE_CUSTOM_WIDGET } from "@ces/XDL/constants";
import { xSiteMessage } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

const mapStateToProps = (state: CustomAppState) => ({
    accountThumbnailsData: state.xAccountThumbnailsData?.data,
    session: getSession(state),
    isAuthenticated: xIsAuthenticated(state),
    billToState: getCurrentBillToState(state),
});

const mapDispatchToProps = {
    loadAccountThumbnailsData,
    loadCurrentBillTo,
};

enum fields {
    itemsList = "itemsList",
    background = "background",
    fullHeight = "fullHeight",
    color = "color",
    responsive = "responsive",
}
const enum thumbnailsFields {
    type = "type",
    title = "title",
    buttonTitle = "buttonTitle",
    link = "link",
    image = "image",
    altText = "altText",
    visibleWithZero = "visibleWithZero",
    showCount = "showCount",
    queryString = "queryString",
}
interface AccountThumbnailsListProps {
    fields: {
        [thumbnailsFields.type]: string;
        [thumbnailsFields.title]: string;
        [thumbnailsFields.buttonTitle]: string;
        [thumbnailsFields.link]: LinkFieldValue;
        [thumbnailsFields.image]: string;
        [thumbnailsFields.altText]: string;
        [thumbnailsFields.visibleWithZero]: boolean;
        [thumbnailsFields.showCount]: boolean;
        [thumbnailsFields.queryString]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.itemsList]: AccountThumbnailsListProps[];
        [fields.background]: string;
        [fields.fullHeight]: boolean;
        [fields.responsive]: boolean;
        [fields.color]: string;
    };
}

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface AccountThumbnailsStyles {
    wrapper?: InjectableCss;
    titleSection?: InjectableCss;
    title?: TypographyProps;
    item?: {
        titleContainer?: InjectableCss;
        title?: TypographyProps;
        link?: LinkPresentationProps;
        number?: TypographyProps;
        image?: LazyImageProps;
        imageWrapper?: InjectableCss;
    };
    itemInfoWrapper?: InjectableCss;
    thumbnailsContainer?: GridContainerProps;
    thumbnailItem?: GridItemProps;
    itemContainer?: InjectableCss;
    greetingTitle?: TypographyProps;
    spinnerWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    responsiveSmallHeadingAndText?: SmallHeadingAndTextStyles;
    widgetWrapper?: InjectableCss;
}

export const accountThumbnailsStyles: AccountThumbnailsStyles = {};

const styles = accountThumbnailsStyles;
const icons = [QuotesQuickDashboard, BackOrdersDashboard, SavedListsDashboard, StatementsDashboard];
const AccountThumbnails: FC<Props> = ({
    fields,
    session,
    accountThumbnailsData,
    isAuthenticated,
    billToState,
    loadAccountThumbnailsData,
    loadCurrentBillTo,
}) => {
    if (!isAuthenticated) {
        return null;
    }
    const billTo = billToState.value;
    useEffect(() => {
        loadAccountThumbnailsData({});
        if (!billToState.value && !billToState.isLoading) {
            loadCurrentBillTo();
        }
    }, []);

    const itemsLinks = useGetLinks(fields.itemsList, o => o.fields.link);

    if (!session || !accountThumbnailsData) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    const smallHeadingAndText = fields.responsive ? styles.responsiveSmallHeadingAndText : styles.smallHeadingAndText;

    return (
        <StyledWrapper {...styles.widgetWrapper} {...{ background: fields.background, fullHeight: fields.fullHeight }}>
            <StyledWrapper
                {...styles.wrapper}
                data-test-selector="AccountThumbnails"
                {...{ responsive: fields.responsive }}
                className={fields.responsive ? "full-width" : ""}
            >
                <StyledWrapper {...styles.infoSection} {...{ color: fields.color }}>
                    <StyledWrapper {...styles.titleSection}>
                        <Typography
                            {...styles.greetingTitle}
                            {...{ responsive: fields.responsive }}
                            data-test-selector="AccountTiles_title"
                        >
                            {xSiteMessage("Custom_AccountTiles_Greeting") || translate("Hello")}
                            {session.userLabel && ","}
                        </Typography>
                        {session.userLabel && (
                            <Typography
                                {...styles.title}
                                {...{ responsive: fields.responsive }}
                                data-test-selector="AccountTiles_subtitle"
                            >
                                {session.userLabel}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <SmallHeadingAndText
                        heading={translate("Customer")}
                        text={`#${billTo?.customerNumber}`}
                        extendedStyles={{ ...smallHeadingAndText }}
                    />
                </StyledWrapper>
                <GridContainer {...styles.thumbnailsContainer}>
                    {fields.itemsList?.map((itemField, index) => {
                        const item = {
                            link: `${itemsLinks?.[index].url}${itemField.fields.queryString || ""}`,
                            type: itemField.fields.type,
                            title: itemField.fields.title,
                            image: itemField.fields.image,
                            altText: itemField.fields.altText || `Account thumbnail icon for ${itemField.fields.title}`,
                            buttonTitle: itemField.fields.buttonTitle,
                        };
                        const numOfThumbnail = accountThumbnailsData[item.type as keyof typeof accountThumbnailsData];
                        if (
                            item.type &&
                            (numOfThumbnail === undefined ||
                                (numOfThumbnail === 0 && !itemField.fields.visibleWithZero))
                        ) {
                            return <React.Fragment key={item.title}></React.Fragment>;
                        }
                        return (
                            <GridItem {...styles.thumbnailItem} {...{ responsive: fields.responsive }} key={item.title}>
                                <StyledWrapper {...styles.itemContainer}>
                                    <StyledWrapper {...styles.item?.imageWrapper}>
                                        {item.image ? (
                                            <LazyImage
                                                {...styles.item?.image}
                                                src={item.image}
                                                altText={item.altText}
                                                imgProps={{ title: item.altText }}
                                                data-test-selector="AccountThumbnails-TileImage"
                                            />
                                        ) : (
                                            <IconMemo src={icons[index]} color="secondary" title={item.altText} />
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.itemInfoWrapper}>
                                        <StyledWrapper {...styles.item?.titleContainer}>
                                            {itemField.fields.showCount &&
                                                !!accountThumbnailsData &&
                                                item.type in accountThumbnailsData && (
                                                    <Typography
                                                        data-test-selector="AccountThumbnails-TileCount"
                                                        {...styles.item?.number}
                                                    >
                                                        {numOfThumbnail}
                                                    </Typography>
                                                )}
                                            <Typography {...styles.item?.title} {...{ numberless: !item.type }}>
                                                {item.title}
                                            </Typography>
                                        </StyledWrapper>
                                        <Link href={item.link} {...styles.item?.link}>
                                            {item.buttonTitle}
                                        </Link>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                        );
                    })}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountThumbnails),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: fields.itemsList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: thumbnailsFields.type,
                        displayName: "Thumbnail type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "", value: "" },
                            { displayName: "Quotes", value: "numOfOpenQuotes" },
                            { displayName: "Back Orders", value: "numOfBackOrders" },
                            { displayName: "WishLists", value: "numOfWishLists" },
                            { displayName: "Statements", value: "numOfStatements" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: thumbnailsFields.showCount,
                        displayName: "Show records count",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isVisible: item => item.fields[thumbnailsFields.type],
                    },
                    {
                        name: thumbnailsFields.visibleWithZero,
                        displayName: "Visible with zero records",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: item => item.fields[thumbnailsFields.type],
                    },
                    {
                        name: thumbnailsFields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: thumbnailsFields.buttonTitle,
                        displayName: "Button title",
                        editorTemplate: "TextField",
                        defaultValue: "View Lists",
                        isRequired: false,
                    },
                    {
                        name: thumbnailsFields.link,
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                    {
                        name: thumbnailsFields.queryString,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: thumbnailsFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: thumbnailsFields.altText,
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
            },

            {
                name: fields.color,
                displayName: "Title Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#fff",
                fieldType: "General",
            },
            {
                name: fields.background,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.fullHeight,
                displayName: "Full Height Background",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.responsive,
                displayName: "Responsive",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
